import { Loading } from "@mnk/frontend/components/Loading";
import { groupBy } from "@mnk/frontend/lib/array";
import { trpc } from "@mnk/frontend/lib/trpc";
import type { FlagAction } from "@mnk/shared/models/flagAction";
import type { FlagColumn } from "@mnk/shared/models/flagColumn";
import type { FlagRow } from "@mnk/shared/models/flagRow";
import { placeTypeMap } from "@mnk/shared/models/placeType";
interface Props {
  quantityId: string;
  onClose: () => void;
}

const Th = ({
  children,
  modified = false,
  stickyLeft = false,
  leftOffset = 0,
}: {
  children: React.ReactNode;
  modified?: boolean;
  stickyLeft?: boolean;
  leftOffset?: number;
}) => {
  return (
    <th
      style={{
        minWidth: "96px",
        ...(stickyLeft ? { position: "sticky", left: `${leftOffset}px` } : {}),
      }}
      className={`py-2 px-3 border-r border-gray-600 last:border-r-0 text-sm text-center font-normal ${
        modified ? "bg-orange-200 text-black" : ""
      } ${stickyLeft ? "bg-gray-700" : ""}`}
    >
      {children}
    </th>
  );
};

const Td = ({
  children,
  stickyLeft = false,
  leftOffset = 0,
}: {
  children: React.ReactNode;
  stickyLeft?: boolean;
  leftOffset?: number;
}) => {
  return (
    <td
      style={{
        minWidth: "96px",
        ...(stickyLeft ? { position: "sticky", left: `${leftOffset}px` } : {}),
      }}
      className={`py-2 px-3 border-t border-r border-gray-200 last:border-r-0 whitespace-nowrap text-sm ${
        stickyLeft ? "bg-white" : ""
      }`}
    >
      {children}
    </td>
  );
};

export const QuantityModal = ({ onClose, quantityId }: Props) => {
  const flagActionsQuery = trpc.getQuantityFlagActions.useQuery({ quantityId });
  const flagRowsQuery = trpc.getQuantityFlagRows.useQuery({ quantityId });
  const flagColumnsQuery = trpc.getQuantityFlagColumns.useQuery({ quantityId });

  const flagActions = flagActionsQuery.data;
  const flagRows = flagRowsQuery.data;
  const flagColumns = flagColumnsQuery.data;

  const groupedFlagActions = groupBy(flagActions ?? [], (flagAction: FlagAction) => flagAction.flagRowId);

  return (
    <div
      className="fixed inset-0 bg-black/40 flex justify-center items-center z-50"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      <div className="w-full h-full p-4">
        <div
          className="bg-white rounded-lg shadow-lg w-full h-full flex flex-col"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onClose();
            }
          }}
        >
          <div className="flex flex-row gap-2 justify-between p-4">
            <h2 className="text-left text-2xl font-bold">数量表</h2>
            <div className="flex justify-end">
              <button type="button" onClick={onClose}>
                閉じる
              </button>
            </div>
          </div>
          {flagActionsQuery.isLoading || flagRowsQuery.isLoading || flagColumnsQuery.isLoading ? (
            <Loading />
          ) : (
            <div className="flex-1 overflow-hidden">
              <div className="h-full overflow-auto">
                <table className="border-collapse">
                  <thead className="bg-gray-700 text-white sticky top-0 z-20">
                    <tr>
                      <Th stickyLeft leftOffset={0}>
                        上下区分
                      </Th>
                      <Th stickyLeft leftOffset={96}>
                        本線・ランプ区分
                      </Th>
                      <Th stickyLeft leftOffset={192}>
                        位置区分
                      </Th>
                      <Th stickyLeft leftOffset={288}>
                        IC JCT SA・PA
                      </Th>
                      <Th stickyLeft leftOffset={384}>
                        ランプ名
                      </Th>
                      <Th stickyLeft leftOffset={480}>
                        KP
                      </Th>
                      <Th stickyLeft leftOffset={576} modified>
                        変更後KP
                      </Th>
                      <Th stickyLeft leftOffset={672}>
                        〜
                      </Th>
                      <Th stickyLeft leftOffset={768}>
                        KP
                      </Th>
                      <Th stickyLeft leftOffset={864} modified>
                        変更後KP
                      </Th>
                      <Th>施工日①</Th>
                      <Th>施工日②</Th>
                      <Th>施工日③</Th>
                      <Th>施工日④</Th>
                      {flagColumns?.map((flagColumn: FlagColumn) => (
                        <>
                          <Th key={`${flagColumn.id}-name`}>
                            {flagColumn.name}
                            <br />
                            {flagColumn.unit}
                          </Th>
                          <Th modified key={`${flagColumn.id}-modifiedName`}>
                            変更後
                            <br />
                            {flagColumn.name}
                            <br />
                            {flagColumn.unit}
                          </Th>
                        </>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {flagRows?.map((flagRow: FlagRow) => {
                      const actions = groupedFlagActions[flagRow.id];
                      return (
                        <tr key={flagRow.id}>
                          <Td stickyLeft leftOffset={0}>
                            {flagRow.roadType === "Upbound" ? "上り" : "下り"}
                          </Td>
                          <Td stickyLeft leftOffset={96}>
                            {flagRow.honsenOrRamp === "Honsen" ? "本線" : "ランプ"}
                          </Td>
                          <Td stickyLeft leftOffset={192}>
                            {placeTypeMap[flagRow.placeType]}
                          </Td>
                          <Td stickyLeft leftOffset={288}>
                            {flagRow.icJCTName}
                          </Td>
                          <Td stickyLeft leftOffset={384}>
                            {flagRow.rampName}
                          </Td>
                          <Td stickyLeft leftOffset={480}>
                            {flagRow.startKiloPost}
                          </Td>
                          <Td stickyLeft leftOffset={576}>
                            {flagRow.modifiedStartKiloPost}
                          </Td>
                          <Td stickyLeft leftOffset={672}>
                            ~
                          </Td>
                          <Td stickyLeft leftOffset={768}>
                            {flagRow.endKiloPost}
                          </Td>
                          <Td stickyLeft leftOffset={864}>
                            {flagRow.modifiedEndKiloPost}
                          </Td>
                          <Td>{flagRow.dateFirst?.toLocaleDateString()}</Td>
                          <Td>{flagRow.dateSecond?.toLocaleDateString()}</Td>
                          <Td>{flagRow.dateThird?.toLocaleDateString()}</Td>
                          <Td>{flagRow.dateFourth?.toLocaleDateString()}</Td>
                          {flagColumns?.map((flagColumn: FlagColumn) => {
                            const flagAction = actions?.find(
                              (flagAction: FlagAction) => flagAction.flagColumnId === flagColumn.id,
                            );
                            return (
                              <>
                                <Td key={`${flagColumn.id}-value`}>
                                  {flagAction?.value ? Number(flagAction?.value).toLocaleString() : ""}
                                </Td>
                                <Td key={`${flagColumn.id}-modifiedValue`}>
                                  {flagAction?.modifiedValue ? Number(flagAction?.modifiedValue).toLocaleString() : ""}
                                </Td>
                              </>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
